import React, { useEffect, useRef, useState } from 'react';
import { ThumbsUp, Share2, MoreHorizontal, Send, ChevronDown, User } from 'lucide-react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { toast } from 'sonner';
import { Story, Comment } from './types';
import { getYouTubeID, getTikTokID, formatViews, formatTimeAgo, getOrCreateGuestId } from './utils/videoUtils';
import StoryCard from './StoryCard';
import Avatar from './Avatar';

// CommentItem component for rendering individual comments with likes and replies
interface CommentItemProps {
  comment: any;
  guestId: string;
  onLike: (commentId: Id<"storyComments">, isLiked: boolean) => void;
  onReply: (commentId: string) => void;
  onToggleReplies: (commentId: string) => void;
  isExpanded: boolean;
  replyingTo: string | null;
  replyText: string;
  onReplyTextChange: (text: string) => void;
  onSubmitReply: (commentId: Id<"storyComments">) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  guestId,
  onLike,
  onReply,
  onToggleReplies,
  isExpanded,
  replyingTo,
  replyText,
  onReplyTextChange,
  onSubmitReply,
}) => {
  // Query like count and liked status for this comment
  const likeCount = useQuery(api.stories.commentLikes.getLikeCount, { commentId: comment._id });
  const isLiked = useQuery(api.stories.commentLikes.isCommentLiked, { commentId: comment._id, guestId });

  // Query replies for this comment
  const replies = useQuery(
    api.stories.comments.getReplies,
    isExpanded ? { parentCommentId: comment._id } : "skip"
  );

  return (
    <div className="flex gap-4">
      <Avatar src={comment.userAvatar} name={comment.userName} size="md" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-sm">{comment.userName}</span>
          <span className="text-xs text-slate-500">{formatTimeAgo(comment.timestamp)}</span>
        </div>
        <p className="text-sm text-slate-800">{comment.text}</p>
        <div className="flex items-center gap-4 mt-2">
          <button
            onClick={() => onLike(comment._id, isLiked ?? false)}
            disabled={isLiked === undefined}
            className={`flex items-center gap-1 text-xs hover:text-slate-800 transition-colors ${isLiked ? 'text-blue-600' : 'text-slate-500'
              }`}
          >
            <ThumbsUp className={`w-3 h-3 ${isLiked ? 'fill-current' : ''}`} />
            {likeCount !== undefined && likeCount > 0 ? likeCount : ''}
          </button>
          <button
            onClick={() => onReply(comment._id)}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800 rounded-full px-2 py-1 hover:bg-slate-100"
          >
            Reply
          </button>
        </div>

        {/* Reply input */}
        {replyingTo === comment._id && (
          <div className="mt-3 flex gap-3">
            <Avatar src={null} name="Guest" size="sm" />
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Add a reply..."
                className="w-full border-b border-slate-200 py-2 text-sm focus:border-black outline-none transition-colors bg-transparent"
                value={replyText}
                onChange={(e) => onReplyTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSubmitReply(comment._id);
                  }
                }}
              />
              <button
                onClick={() => onSubmitReply(comment._id)}
                disabled={!replyText.trim()}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-blue-600 transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Show replies button */}
        {(comment.replyCount ?? 0) > 0 && (
          <button
            onClick={() => onToggleReplies(comment._id)}
            className="mt-2 flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            {isExpanded ? 'Hide' : 'View'} {comment.replyCount ?? 0} {(comment.replyCount ?? 0) === 1 ? 'reply' : 'replies'}
          </button>
        )}

        {/* Replies list */}
        {isExpanded && replies && replies.length > 0 && (
          <div className="mt-4 ml-8 space-y-4 border-l-2 border-slate-200 pl-4">
            {replies.map((reply: any) => (
              <div key={reply._id} className="flex gap-3">
                <Avatar src={reply.userAvatar} name={reply.userName} size="sm" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{reply.userName}</span>
                    <span className="text-xs text-slate-500">{formatTimeAgo(reply.timestamp)}</span>
                  </div>
                  <p className="text-sm text-slate-800">{reply.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


interface VideoPlayerViewProps {
  story: Story;
  relatedStories: Story[];
  onStoryClick: (story: Story) => void;
  onAddComment: (storyId: string, text: string) => void;
  brandId?: Id<"brands">; // Add brandId prop
}

type Shape = 'portrait' | 'landscape';

/**
 * Which way the picture runs, before the video itself can say: a YouTube link
 * is wide unless it is a Short, TikTok is tall, and an upload is a phone video
 * (tall) until its metadata says otherwise.
 */
function guessShape(story: Story): Shape {
  if (story.videoType === 'youtube') return /\/shorts\//i.test(story.videoUrl) ? 'portrait' : 'landscape';
  return 'portrait';
}

/** What scrolls this view: the story pop-up on the shop, or the page itself. */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

const isTextField = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

/**
 * A story, watched the way YouTube does it (Frank, 23 Sep 2026): "the upper
 * section stays stationary and the other parts scroll, so I can read comments
 * or see the next videos while the video plays".
 *
 * Each story gets a fresh player (the `key`): an uploaded video names its file
 * on a <source>, and a <video> never reloads when a <source> changes, so a
 * story picked from Up Next used to keep playing the previous one under the
 * new title. A story picked from Up Next also starts at its top, like a page.
 */
const VideoPlayerView: React.FC<VideoPlayerViewProps> = (props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const shownId = useRef(props.story.id);
  useEffect(() => {
    if (shownId.current === props.story.id) return;
    shownId.current = props.story.id;
    scrollParentOf(anchorRef.current)?.scrollTo({ top: 0 });
  }, [props.story.id]);

  return (
    <div ref={anchorRef}>
      <StoryWatch key={props.story.id} {...props} />
    </div>
  );
};

const StoryWatch: React.FC<VideoPlayerViewProps> = ({
  story,
  relatedStories,
  onStoryClick,
  onAddComment,
  brandId
}) => {
  const [commentText, setCommentText] = useState('');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // The frame follows the video: an upload reports its size once its
  // metadata arrives; until then, the guess.
  const [measuredShape, setMeasuredShape] = useState<Shape | null>(null);
  const portrait = (measuredShape ?? guessShape(story)) === 'portrait';

  // Typing a comment on a phone: the keyboard takes half the screen, and a
  // pinned player would cover the field, so it lets go until the field is left.
  const [typing, setTyping] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());

  // Get guest ID for likes
  const guestId = getOrCreateGuestId();

  // Helper function to get video-specific URL
  const getVideoUrl = () => {
    if (typeof window === 'undefined') return '';
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?video=${story._id || story.id}`;
  };

  // Query follower count
  const followerCount = useQuery(
    api.brands.social.getFollowerCount,
    brandId ? { brandId } : "skip"
  );

  // Query if following
  const isFollowing = useQuery(
    api.brands.social.isFollowing,
    brandId ? { brandId } : "skip"
  );

  // Query like count
  const likeCount = useQuery(
    api.stories.social.getLikeCount,
    story._id ? { storyId: story._id as Id<"stories"> } : "skip"
  );

  // Query if liked
  const isLiked = useQuery(
    api.stories.social.isLiked,
    story._id ? { storyId: story._id as Id<"stories">, guestId } : "skip"
  );

  // Mutations
  const followMutation = useMutation(api.brands.social.followBrand);
  const unfollowMutation = useMutation(api.brands.social.unfollowBrand);
  const likeMutation = useMutation(api.stories.social.likeStory);
  const unlikeMutation = useMutation(api.stories.social.unlikeStory);

  // Comment mutations
  const addCommentMutation = useMutation(api.stories.comments.addComment);
  const likeCommentMutation = useMutation(api.stories.commentLikes.likeComment);
  const unlikeCommentMutation = useMutation(api.stories.commentLikes.unlikeComment);

  // Query comments
  const comments = useQuery(
    api.stories.comments.getComments,
    story._id ? { storyId: story._id as Id<"stories"> } : "skip"
  );

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!brandId) return;

    try {
      if (isFollowing) {
        await unfollowMutation({ brandId });
        toast.success("Unfollowed successfully");
      } else {
        await followMutation({ brandId });
        toast.success("Following brand");
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Something went wrong";
      if (errorMessage.includes("sign in")) {
        toast.error(errorMessage, {
          action: {
            label: "Sign In",
            onClick: () => window.location.href = "/signin",
          },
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  // Handle like/unlike
  const handleLikeToggle = async () => {
    if (!story._id) return;

    try {
      if (isLiked) {
        await unlikeMutation({ storyId: story._id as Id<"stories">, guestId });
      } else {
        await likeMutation({ storyId: story._id as Id<"stories">, guestId });
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update like");
    }
  };

  // Handle share
  const handleShare = () => {
    setShowShareDialog(true);
  };

  const handleCopyLink = () => {
    const url = getVideoUrl();
    navigator.clipboard.writeText(url);
    toast.success("Video link copied to clipboard!");
    setShowShareDialog(false);
  };

  const handleShareWhatsApp = () => {
    const url = getVideoUrl();
    const text = encodeURIComponent(`Check out this video: ${story.title}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareDialog(false);
  };

  const handleShareFacebook = () => {
    const url = getVideoUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    setShowShareDialog(false);
  };

  const handleShareTwitter = () => {
    const url = getVideoUrl();
    const text = encodeURIComponent(`Check out this video: ${story.title}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, '_blank');
    setShowShareDialog(false);
  };

  const handleShareEmail = () => {
    const url = getVideoUrl();
    const subject = encodeURIComponent(`Check out: ${story.title}`);
    const body = encodeURIComponent(`I thought you might like this video:\n\n${story.title}\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowShareDialog(false);
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (commentText.trim()) {
      console.log("VideoPlayerView submitting comment:", { storyId: story.id, text: commentText });
      onAddComment(story.id, commentText);
      setCommentText('');
    }
  };

  // Handle comment like/unlike
  const handleCommentLike = async (commentId: Id<"storyComments">, isLiked: boolean) => {
    try {
      if (isLiked) {
        await unlikeCommentMutation({ commentId, guestId });
      } else {
        await likeCommentMutation({ commentId, guestId });
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update like");
    }
  };

  // Handle reply submission
  const handleAddReply = async (parentCommentId: Id<"storyComments">) => {
    const text = replyText[parentCommentId];
    if (!text || !text.trim() || !story._id) return;

    try {
      await addCommentMutation({
        storyId: story._id as Id<"stories">,
        text: text.trim(),
        guestId,
        guestName: "Guest",
        guestAvatar: "",
        parentCommentId,
      });

      // Clear reply text and close reply input
      setReplyText(prev => ({ ...prev, [parentCommentId]: "" }));
      setReplyingTo(null);
      toast.success("Reply added");
    } catch (error: any) {
      toast.error(error?.message || "Failed to add reply");
    }
  };

  // Toggle replies visibility
  const toggleReplies = (commentId: string) => {
    setExpandedReplies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const renderVideoPlayer = () => {
    if (story.videoType === 'youtube') {
      const videoId = getYouTubeID(story.videoUrl);
      return (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title={story.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    } else if (story.videoType === 'tiktok') {
      const videoId = getTikTokID(story.videoUrl);
      if (videoId) {
        // Standard TikTok video URL with extracted ID
        // TikTok videos are portrait (9:16), so we handle them specially
        return (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.tiktok.com/embed/v2/${videoId}`}
            title={story.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      } else {
        // Fallback for short URLs or other formats
        return (
          <div className="absolute inset-0 bg-slate-900 flex items-center justify-center text-white p-4 md:p-8">
            <div className="text-center">
              <svg className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
              <p className="text-base md:text-lg font-semibold mb-2">TikTok Video</p>
              <p className="text-xs md:text-sm text-slate-400 mb-3 md:mb-4">This video requires the full TikTok URL to embed</p>
              <a
                href={story.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 md:px-6 py-2 md:py-3 bg-white text-slate-900 rounded-full text-sm md:text-base font-semibold hover:bg-slate-100 transition-colors"
              >
                Watch on TikTok
              </a>
            </div>
          </div>
        );
      }
    } else {
      // An uploaded video comes off our Bunny CDN, which stores blobs under
      // extensionless UUID keys and therefore serves EVERY file as
      // `application/octet-stream` (verified: HEAD on a stored mp4). Chrome
      // sniffs the bytes and plays anyway; Safari trusts the header and
      // refuses — the same trap that made voice notes silent on iPhone.
      // Declaring the type on a <source> is the hint Safari needs. iPhone
      // uploads are QuickTime (.mov): four of Korean Beauty's six stories.
      // A <source> whose type a browser says it cannot play is skipped
      // silently (Chrome answers "" for video/quicktime yet plays the file),
      // so the typed <source> is always followed by an untyped one: a browser
      // that passes over the hint still gets the video.
      const declared =
        story.videoContentType === "video/mp4" ||
        story.videoContentType === "video/webm" ||
        story.videoContentType === "video/quicktime"
          ? story.videoContentType
          : /\.mp4($|\?)/i.test(story.videoUrl)
            ? "video/mp4"
            : /\.mov($|\?)/i.test(story.videoUrl)
              ? "video/quicktime"
              : null;
      return (
        <video
          className="absolute inset-0 w-full h-full bg-black object-contain"
          controls
          autoPlay
          // Without this an iPhone takes every video fullscreen on play.
          playsInline
          poster={story.thumbnailUrl || undefined}
          onLoadedMetadata={(e) => {
            const { videoWidth: w, videoHeight: h } = e.currentTarget;
            if (w && h) setMeasuredShape(h > w * 1.05 ? 'portrait' : 'landscape');
          }}
          {...(declared ? {} : { src: story.videoUrl })}
        >
          {declared ? <source src={story.videoUrl} type={declared} /> : null}
          {declared ? <source src={story.videoUrl} /> : null}
        </video>
      );
    }
  };

  return (
    <div
      className={`w-full lg:grid lg:gap-x-8 ${portrait
        ? 'lg:grid-cols-[auto_minmax(0,1fr)] lg:grid-rows-[auto_1fr]'
        : 'lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]'
        }`}
      onFocusCapture={(e) => { if (isTextField(e.target)) setTyping(true); }}
      onBlurCapture={(e) => { if (isTextField(e.target)) setTyping(false); }}
    >
      {/* On a phone this wrapper has no box of its own, so the pinned player
          stays pinned over EVERYTHING below it, Up Next included. On a
          computer a wide video's player and details form the left column
          (the player pinned over the comments), and a tall video's player
          and details become the grid's own cells: the player on the left,
          the rest scrolling on the right. */}
      <div className={portrait ? 'contents' : 'contents lg:block lg:min-w-0'}>
        {/* The player: pinned to the top while the rest scrolls under it. */}
        <div
          className={`${typing ? 'relative lg:sticky' : 'sticky'} top-0 z-30 bg-black ${portrait
            ? 'lg:top-6 lg:self-start lg:[grid-column:1] lg:[grid-row:1/span_2] lg:bg-transparent'
            : 'lg:bg-white lg:-mt-4 lg:pt-4 lg:pb-3'
            }`}
        >
          <div
            className={`relative mx-auto w-full overflow-hidden bg-black ${portrait
              // Tall: at most about half a phone's height, so there is room
              // to scroll beneath it; on a computer, the window's height.
              ? 'h-[min(55vh,177.78vw)] supports-[height:1svh]:h-[min(55svh,177.78vw)] lg:h-[calc(100vh-3rem)] lg:max-h-[900px] lg:w-auto lg:aspect-[9/16] lg:rounded-2xl'
              : 'aspect-video max-h-[60vh] supports-[height:1svh]:max-h-[60svh] lg:max-h-[68vh] lg:rounded-xl'
              }`}
          >
            {renderVideoPlayer()}
          </div>
        </div>

        {/* Title, actions, description and comments */}
        <div className={`px-3 sm:px-4 pt-3 lg:px-0 ${portrait ? 'lg:pt-0 lg:[grid-column:2] lg:[grid-row:1] lg:min-w-0' : 'lg:pt-1'}`}>
          {/* Video Title & Primary Actions */}
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 mb-2">{story.title}</h1>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 pb-3 md:pb-4 border-b border-slate-200">
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <Avatar src={story.authorAvatar} name={story.author} size="md" />
              <div>
                <h3 className="font-semibold text-sm md:text-base text-slate-900">{story.author}</h3>
                <p className="text-xs text-slate-500">
                  {followerCount !== undefined ? formatViews(followerCount) : "..."} followers
                </p>
              </div>
              <button
                onClick={handleFollowToggle}
                disabled={isFollowing === undefined}
                className="ml-2 md:ml-4 px-3 md:px-4 py-1.5 md:py-2 bg-slate-900 text-white text-xs md:text-sm font-medium rounded-full hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isFollowing ? "Following" : "Follow"}
              </button>
              <button
                onClick={() => {
                  const baseUrl = window.location.origin + window.location.pathname;
                  window.location.href = baseUrl;
                }}
                className="px-3 md:px-4 py-1.5 md:py-2 border border-slate-300 text-slate-700 text-xs md:text-sm font-medium rounded-full hover:bg-slate-50 transition-colors"
              >
                Visit Shop
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleLikeToggle}
                disabled={isLiked === undefined}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isLiked
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-slate-100 hover:bg-slate-200'
                  }`}
              >
                <ThumbsUp className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                <span>{likeCount !== undefined ? formatViews(likeCount) : "..."}</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-medium transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
              <button className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Share Dialog */}
          {showShareDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowShareDialog(false)}>
              <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold mb-4">Share this video</h3>
                <div className="space-y-2">
                  <button
                    onClick={handleShareWhatsApp}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    <span className="font-medium">WhatsApp</span>
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">Copy link</span>
                  </button>
                  <button
                    onClick={handleShareFacebook}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="font-medium">Facebook</span>
                  </button>
                  <button
                    onClick={handleShareTwitter}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-sky-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    <span className="font-medium">Twitter</span>
                  </button>
                  <button
                    onClick={handleShareEmail}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">Email</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowShareDialog(false)}
                  className="mt-4 w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Description Box */}
          <div className="mt-3 md:mt-4 bg-slate-50 p-3 md:p-4 rounded-lg md:rounded-xl cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
            <div className="flex gap-2 text-xs md:text-sm font-semibold text-slate-900 mb-1">
              <span>{formatViews(story.views)} views</span>
              <span>•</span>
              <span>{formatTimeAgo(story.uploadDate)}</span>
            </div>
            <p className={`text-xs md:text-sm text-slate-700 leading-relaxed ${!descriptionExpanded && 'line-clamp-2'}`}>
              {story.description}
            </p>
            <button className="text-xs md:text-sm font-medium text-slate-600 mt-1">
              {descriptionExpanded ? 'Show less' : '...more'}
            </button>
          </div>

          {/* Comments Section */}
          <div className="mt-6 md:mt-8">
            <div className="flex items-center gap-2 mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-bold text-slate-900">Comments</h3>
              <span className="text-slate-500 text-base md:text-lg">
                {comments !== undefined ? comments.length : '...'}
              </span>
            </div>

            {/* Comment Input */}
            <div className="flex gap-3 md:gap-4 mb-6 md:mb-8">
              <Avatar src={null} name="Guest" size="md" />
              <form onSubmit={handleSubmitComment} className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Add a comment..."
                  className="w-full border-b border-slate-200 py-2 text-sm md:text-base focus:border-black outline-none transition-colors bg-transparent"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={!commentText.trim()}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-blue-600 transition-all"
                >
                  <Send className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </form>
            </div>

            {/* Comment List */}
            <div className="space-y-6">
              {comments === undefined ? (
                <div className="text-center py-8 text-slate-500">
                  <p>Loading comments...</p>
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment: any) => (
                  <CommentItem
                    key={comment._id}
                    comment={comment}
                    guestId={guestId}
                    onLike={handleCommentLike}
                    onReply={(commentId) => setReplyingTo(commentId)}
                    onToggleReplies={toggleReplies}
                    isExpanded={expandedReplies.has(comment._id)}
                    replyingTo={replyingTo}
                    replyText={replyText[comment._id] || ""}
                    onReplyTextChange={(text) => setReplyText(prev => ({ ...prev, [comment._id]: text }))}
                    onSubmitReply={handleAddReply}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>No comments yet. Be the first to share your thoughts!</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Up Next: the sidebar beside a wide video; under the details beside a tall one. */}
      <aside
        className={`px-3 sm:px-4 lg:px-0 mt-8 pb-6 ${portrait
          ? 'lg:[grid-column:2] lg:[grid-row:2] lg:min-w-0'
          : 'lg:mt-0 lg:[grid-column:2] lg:[grid-row:1]'
          }`}
      >
        <h3 className="font-bold text-base md:text-lg mb-3 md:mb-4 text-slate-900">Up Next</h3>
        <div className="flex flex-col gap-2 md:gap-0">
          {relatedStories.map(s => (
            <StoryCard
              key={s.id}
              story={s}
              onClick={onStoryClick}
              compact={true}
            />
          ))}
        </div>
      </aside>
    </div>
  );
};

export default VideoPlayerView;
